import { useState, useMemo } from 'react'
import { useParams, Link } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { 
  getSubsystems, createSubsystem, deleteSubsystem,
  SubsystemStatus, STATUS_DEFINITIONS 
} from '../../api/subsystems'
import { getProject } from '../../api/projects'
import { 
  Plus, Search, ArrowLeft, BookOpen, CheckSquare, 
  Trash2, Key, FileText, Phone, Layers
} from 'lucide-react'

export default function SubsystemsPage() {
  const { projectId } = useParams<{ projectId: string }>()
  const projectIdNum = Number(projectId)
  const queryClient = useQueryClient()

  const [searchQuery, setSearchQuery] = useState('')
  const [selectedStatus, setSelectedStatus] = useState<SubsystemStatus | 'all'>('all')
  const [showAddModal, setShowAddModal] = useState(false)

  // Form states for creating a subsystem
  const [newName, setNewName] = useState('')
  const [newDescription, setNewDescription] = useState('')
  const [newStatus, setNewStatus] = useState<SubsystemStatus>('operational')
  const [newContractInfo, setNewContractInfo] = useState('')
  const [newCredentialsInfo, setNewCredentialsInfo] = useState('')
  const [newContactsInfo, setNewContactsInfo] = useState('')

  // Load project details
  const { data: project } = useQuery({
    queryKey: ['project', projectId],
    queryFn: () => getProject(projectIdNum),
    enabled: !!projectIdNum,
  })

  // Load subsystems list
  const { data: subsystems = [], isLoading } = useQuery({
    queryKey: ['subsystems', projectIdNum],
    queryFn: () => getSubsystems(projectIdNum),
    enabled: !!projectIdNum,
  })

  // Create mutation
  const createMutation = useMutation({
    mutationFn: createSubsystem,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['subsystems', projectIdNum] })
      setShowAddModal(false)
      // reset form
      setNewName('')
      setNewDescription('')
      setNewStatus('operational')
      setNewContractInfo('')
      setNewCredentialsInfo('')
      setNewContactsInfo('')
    },
    onError: (err: any) => {
      alert(err.response?.data?.error || 'Ошибка при создании подсистемы')
    }
  })

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: deleteSubsystem,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['subsystems', projectIdNum] })
    },
    onError: (err: any) => {
      alert(err.response?.data?.error || 'Ошибка при удалении подсистемы')
    }
  })

  const handleCreateSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!newName.trim()) {
      alert('Укажите название инженерной системы')
      return
    }
    createMutation.mutate({
      project_id: projectIdNum,
      name: newName.trim(),
      description: newDescription.trim() || undefined,
      status: newStatus,
      contract_info: newContractInfo.trim() || undefined,
      credentials_info: newCredentialsInfo.trim() || undefined,
      contacts_info: newContactsInfo.trim() || undefined,
    })
  }

  const handleDelete = (id: number, name: string) => {
    if (confirm(`Удалить инженерную систему «${name}»? Все посты бортжурнала этой системы также будут удалены.`)) {
      deleteMutation.mutate(id)
    }
  }

  // Filtered subsystems
  const filteredSubsystems = useMemo(() => {
    return subsystems.filter(sub => {
      if (selectedStatus !== 'all' && sub.status !== selectedStatus) return false
      if (searchQuery.trim()) {
        const q = searchQuery.toLowerCase()
        const matchName = sub.name.toLowerCase().includes(q)
        const matchDesc = sub.description?.toLowerCase().includes(q) || false
        const matchContract = sub.contract_info?.toLowerCase().includes(q) || false
        if (!matchName && !matchDesc && !matchContract) return false
      }
      return true
    })
  }, [subsystems, selectedStatus, searchQuery])

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 bg-white p-6 rounded-xl border border-gray-200 shadow-sm">
        <div>
          <Link
            to={`/projects/${projectId}`}
            className="text-sm font-medium text-blue-600 hover:text-blue-800 flex items-center gap-1 mb-2"
          >
            <ArrowLeft className="w-4 h-4" />
            К проекту: {project?.name || 'Проект'}
          </Link>
          <div className="flex items-center gap-3">
            <span className="text-3xl">🏢</span>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Инженерные системы объекта</h1>
              <p className="text-sm text-gray-500 mt-0.5">
                Паспорта систем, договоры, доступы, контакты служб и персональный бортжурнал по каждому разделу
              </p>
            </div>
          </div>
        </div>

        <button
          onClick={() => setShowAddModal(true)}
          className="inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-semibold shadow-sm transition-colors shrink-0"
        >
          <Plus className="w-4 h-4" />
          Добавить систему
        </button>
      </div>

      {/* Filters bar */}
      <div className="bg-white p-4 rounded-xl border border-gray-200 shadow-sm flex flex-col sm:flex-row items-center gap-3">
        <div className="relative flex-1 w-full">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Поиск по названию, описанию, договорам..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-9 pr-4 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>

        <select
          value={selectedStatus}
          onChange={(e) => setSelectedStatus(e.target.value as any)}
          className="w-full sm:w-56 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
        >
          <option value="all">Все статусы ({subsystems.length})</option>
          {Object.entries(STATUS_DEFINITIONS).map(([st, def]) => {
            const count = subsystems.filter(s => s.status === st).length
            return (
              <option key={st} value={st}>{def.label} ({count})</option>
            )
          })}
        </select>
      </div>

      {/* Subsystems Cards Grid */}
      {isLoading ? (
        <div className="text-center py-12 text-gray-500">Загрузка инженерных систем...</div>
      ) : filteredSubsystems.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-xl border border-gray-200 p-8 shadow-sm">
          <div className="text-5xl mb-3">🛠️</div>
          <h3 className="text-lg font-bold text-gray-800">Инженерные системы не найдены</h3>
          <p className="text-gray-500 text-sm mt-1 max-w-md mx-auto">
            {searchQuery || selectedStatus !== 'all'
              ? 'Попробуйте изменить параметры фильтрации'
              : 'Добавьте первую подсистему объекта (например, «Интернет», «Локальная сеть» или «Кондиционирование»), чтобы вести её спецификацию, доступы и персональный бортжурнал.'}
          </p>
          {!searchQuery && selectedStatus === 'all' && (
            <button
              onClick={() => setShowAddModal(true)}
              className="mt-4 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-semibold transition-colors"
            >
              + Добавить систему
            </button>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {filteredSubsystems.map((sub) => {
            const statusDef = STATUS_DEFINITIONS[sub.status] || STATUS_DEFINITIONS.operational

            return (
              <div
                key={sub.id}
                className="bg-white rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-shadow flex flex-col overflow-hidden"
              >
                {/* Card Top */}
                <div className="p-5 pb-4 border-b border-gray-100 flex-1">
                  <div className="flex items-start justify-between gap-3 mb-2.5">
                    <div className="flex items-center gap-3">
                      <div className="p-2.5 bg-blue-50 text-blue-600 rounded-xl border border-blue-100">
                        <Layers className="w-5 h-5" />
                      </div>
                      <div>
                        <Link
                          to={`/projects/${projectId}/systems/${sub.id}`}
                          className="block text-base font-bold text-gray-900 hover:text-blue-600 transition-colors line-clamp-1"
                        >
                          {sub.name}
                        </Link>
                        <span className="text-[11px] text-gray-400 font-mono">
                          ID #{sub.id}
                        </span>
                      </div>
                    </div>

                    <span className={`text-[11px] font-medium px-2.5 py-0.5 rounded-full border ${statusDef.badgeClass} shrink-0`}>
                      {statusDef.label}
                    </span>
                  </div>

                  {sub.description && (
                    <p className="text-xs text-gray-600 line-clamp-2 mt-2">
                      {sub.description}
                    </p>
                  )}

                  {/* Passport indicators */}
                  <div className="mt-4 flex flex-wrap gap-2 text-[11px] text-gray-500">
                    {sub.contract_info && (
                      <span className="inline-flex items-center gap-1 bg-amber-50 text-amber-800 border border-amber-200 px-2 py-0.5 rounded">
                        <FileText className="w-3 h-3" /> Договор
                      </span>
                    )}
                    {sub.credentials_info && (
                      <span className="inline-flex items-center gap-1 bg-purple-50 text-purple-800 border border-purple-200 px-2 py-0.5 rounded">
                        <Key className="w-3 h-3" /> Доступы
                      </span>
                    )}
                    {sub.contacts_info && (
                      <span className="inline-flex items-center gap-1 bg-blue-50 text-blue-800 border border-blue-200 px-2 py-0.5 rounded">
                        <Phone className="w-3 h-3" /> Контакты
                      </span>
                    )}
                  </div>
                </div>

                {/* Card Stats Footer */}
                <div className="bg-gray-50 px-5 py-3 border-t border-gray-150 flex items-center justify-between text-xs text-gray-600">
                  <div className="flex items-center gap-4">
                    {/* Logbook / Posts Badge */}
                    <Link
                      to={`/projects/${projectId}/systems/${sub.id}?tab=logbook`}
                      className="flex items-center gap-1.5 hover:text-blue-600 font-medium transition-colors"
                      title="Бортжурнал подсистемы"
                    >
                      <BookOpen className="w-3.5 h-3.5 text-blue-500" />
                      <span>{sub.post_count || 0} постов</span>
                    </Link>

                    {/* Tasks Badge */}
                    <Link
                      to={`/projects/${projectId}/systems/${sub.id}?tab=tasks`}
                      className="flex items-center gap-1.5 hover:text-blue-600 font-medium transition-colors"
                      title="Задачи по подсистеме"
                    >
                      <CheckSquare className="w-3.5 h-3.5 text-emerald-500" />
                      <span>{sub.active_task_count || 0} задач</span>
                    </Link>
                  </div>

                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => handleDelete(sub.id, sub.name)}
                      className="p-1 text-gray-400 hover:text-red-600 rounded transition-colors"
                      title="Удалить систему"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                    <Link
                      to={`/projects/${projectId}/systems/${sub.id}`}
                      className="px-2.5 py-1 bg-white border border-gray-300 rounded font-semibold text-gray-700 hover:bg-gray-100 transition-colors"
                    >
                      Открыть →
                    </Link>
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      )}

      {/* Modal: Add Subsystem */}
      {showAddModal && (
        <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-xs flex items-center justify-center p-4 overflow-y-auto">
          <div className="bg-white rounded-2xl max-w-xl w-full p-6 shadow-2xl space-y-5 border border-gray-150 my-8">
            <div className="flex items-center justify-between border-b border-gray-100 pb-3">
              <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                <span>🏢</span>
                <span>Добавить инженерную систему</span>
              </h3>
              <button
                onClick={() => setShowAddModal(false)}
                className="text-gray-400 hover:text-gray-600 text-xl font-bold p-1"
              >
                ×
              </button>
            </div>

            <form onSubmit={handleCreateSubmit} className="space-y-4">
              {/* Name */}
              <div>
                <label className="block text-xs font-bold text-gray-700 uppercase tracking-wide mb-1">
                  Название системы <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  placeholder="например, Интернет, Локальная сеть, Кондиционирование..."
                  required
                  autoFocus
                  className="w-full px-3.5 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              {/* Status */}
              <div>
                <label className="block text-xs font-bold text-gray-700 uppercase tracking-wide mb-1">
                  Текущий статус
                </label>
                <select
                  value={newStatus}
                  onChange={(e) => setNewStatus(e.target.value as SubsystemStatus)}
                  className="w-full px-3.5 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                >
                  {Object.entries(STATUS_DEFINITIONS).map(([st, def]) => (
                    <option key={st} value={st}>{def.label}</option>
                  ))}
                </select>
              </div>

              {/* Description */}
              <div>
                <label className="block text-xs font-bold text-gray-700 uppercase tracking-wide mb-1">
                  Описание / Назначение
                </label>
                <textarea
                  rows={2}
                  value={newDescription}
                  onChange={(e) => setNewDescription(e.target.value)}
                  placeholder="Краткое описание оборудования, архитектуры и зон ответственности..."
                  className="w-full px-3.5 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              {/* Initial Passport Info: Contract */}
              <div>
                <label className="block text-xs font-bold text-gray-700 uppercase tracking-wide mb-1">
                  Договоры, тарифы, лицевые счета (опционально)
                </label>
                <textarea
                  rows={2}
                  value={newContractInfo}
                  onChange={(e) => setNewContractInfo(e.target.value)}
                  placeholder="Провайдер Ростелеком, Договор №12345, Л/С 987654, 500 Мбит/с..."
                  className="w-full px-3.5 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              {/* Initial Passport Info: Credentials */}
              <div>
                <label className="block text-xs font-bold text-gray-700 uppercase tracking-wide mb-1">
                  Учетные данные, логины/пароли, IP (опционально)
                </label>
                <textarea
                  rows={2}
                  value={newCredentialsInfo}
                  onChange={(e) => setNewCredentialsInfo(e.target.value)}
                  placeholder="IP: 192.168.1.1, login: admin, пароль в менеджере паролей..."
                  className="w-full px-3.5 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              {/* Initial Passport Info: Contacts */}
              <div>
                <label className="block text-xs font-bold text-gray-700 uppercase tracking-wide mb-1">
                  Контакты сервисных служб и инженеров (опционально)
                </label>
                <textarea
                  rows={2}
                  value={newContactsInfo}
                  onChange={(e) => setNewContactsInfo(e.target.value)}
                  placeholder="Сервисный инженер: +7 (999) 000-00-00..."
                  className="w-full px-3.5 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div className="flex items-center justify-end gap-3 pt-3 border-t border-gray-100">
                <button
                  type="button"
                  onClick={() => setShowAddModal(false)}
                  className="px-4 py-2 text-sm text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg font-medium transition-colors"
                >
                  Отмена
                </button>
                <button
                  type="submit"
                  disabled={createMutation.isPending || !newName.trim()}
                  className="px-5 py-2 text-sm text-white bg-blue-600 hover:bg-blue-700 rounded-lg font-semibold shadow-sm transition-colors disabled:opacity-50"
                >
                  {createMutation.isPending ? 'Создание...' : 'Создать систему'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}
